import { FC } from 'react';
import { smallCurrencyFormat } from 'common/formats/formats';
import { GridDateProps } from 'common/types/scalarSpreadsheet';
import { withMoneyChip } from 'components/CellAnnotationValueViewer/components/MoneyChipValueViewer';
import { GridDate, GridDateEditor } from 'components/FeaturedSpreadsheet/components';
import { formatNumbers } from 'utilities';
import { CashFlowColumnAlias, CashFlowColumnValue } from './constants';
import CflExclusionCheckbox from '../components/CflExclusionCheckbox';
import EntityDataEditor from '../data-editors/EntityDataEditor';
import OwnershipDataEditor from '../data-editors/OwnershipDataEditor';
import SecurityDataEditor from '../data-editors/SecurityDataEditor';
import DateValueViewer from '../value-viewers/DateValueViewer';
import EntityValueViewer from '../value-viewers/EntityValueViewer';
import OwnershipValueViewer from '../value-viewers/OwnershipValueViewer';
import SecurityValueViewer from '../value-viewers/SecurityValueViewer';

// CFL Amount: 2dp display, but allow up to 15dp when editing (matches backend
// `DecimalField(max_digits=31, decimal_places=15)` and §4.4 of the design spec).
export const cashFlowAmountFormat = {
  ...smallCurrencyFormat,
  maximumFractionDigitsWhenEditing: 15,
};

const PlainAmountViewer = (props: any) => {
  const currency = props.cell.currency ?? props.cell.customFormat?.currency;
  return (
    <span>
      {formatNumbers({
        format: props.cell.format,
        currency,
        currencyCode: props.cell.currencyCode,
        value: props.cell.value,
      } as any)}
    </span>
  );
};

// `is_excludable === false` (UNREALIZED_VALUE virtual rows) bypass the checkbox;
// they have no per-row source object and always count.
// The wrapper emits `data-cfl-amount-cell` + source identifiers so E2E can pick
// rows deterministically (rows reorder by transaction date). `display: contents`
// makes the wrapper layout-transparent so flex/width behavior is unchanged.
// Removing `display: contents` will reintroduce the wrapper as a block-level
// parent of CflExclusionCheckbox - verify Amount-column layout before changing.
const AmountValueViewer = (props: any) => {
  const { cell } = props;
  const isUnrealized = cell.is_excludable === false;
  return (
    <div
      data-cfl-amount-cell=""
      data-cfl-source-kind={cell.source_kind ?? (cell.is_virtual ? 'virtual' : 'manual')}
      data-cfl-source-id={cell.source_id ?? ''}
      data-cfl-is-virtual={cell.is_virtual ? 'true' : 'false'}
      data-cfl-is-excludable={isUnrealized ? 'false' : 'true'}
      style={{ display: 'contents' }}>
      {isUnrealized ? <PlainAmountViewer {...props} /> : <CflExclusionCheckbox {...props} />}
    </div>
  );
};

// Module-scope memoized chip-wrapped Amount viewer. Hoisting keeps the
// component identity stable across CFL parses so React reconciliation +
// memoization aren't broken by per-row re-creation. Exported so the parser
// uses the same identity whether the cell is annotation-eligible or not.
export const AmountValueViewerWithChip = withMoneyChip(AmountValueViewer);

// Define the column structure for cash flow transactions.
// Amount and Currency are populated per-row by the parser from `row.currency`
// (sourced from cap_table.company.irr_currency). The column-level
// customFormat/defaultValue is intentionally omitted so the parser's per-cell
// override fully controls display currency.
export const getCashFlowColumns = () => [
  {
    alias: CashFlowColumnAlias.DATE_OF_TRANSACTION,
    value: CashFlowColumnValue.DATE_OF_TRANSACTION,
    gridType: 'gridDate',
    dbType: 'dbDate',
    isRequired: true,
    displayUndefined: true,
    gridDateComponent: GridDate as FC<GridDateProps>,
    dataEditor: (props: any) => <GridDateEditor {...props} />,
    valueViewer: (props: any) => <DateValueViewer {...props} />,
  },
  {
    alias: CashFlowColumnAlias.AMOUNT,
    value: CashFlowColumnValue.AMOUNT,
    gridType: 'number',
    dbType: 'string',
    format: cashFlowAmountFormat,
    isRequired: true,
    displayUndefined: true,
    // Required for CFL: CflExclusionCheckbox (via NumberMultipleCheckbox) auto-disables on <=0 numbers
    // unless insideLedger is set, and CFL is full of negatives (investments).
    insideLedger: true,
    valueViewer: AmountValueViewer,
  },
  {
    alias: CashFlowColumnAlias.TRANSACTION_TYPE,
    value: CashFlowColumnValue.TRANSACTION_TYPE,
    gridType: 'string',
    dbType: 'string',
    isRequired: true,
    displayUndefined: true,
    // FastValueViewer shows a hover tooltip with the full value when truncated.
    className: 'show-tooltip',
  },
  {
    alias: CashFlowColumnAlias.OWNER,
    value: CashFlowColumnValue.OWNER,
    gridType: 'string',
    dbType: 'string',
    isRequired: true,
    displayUndefined: true,
    valueViewer: (props: any) => <OwnershipValueViewer {...props} />,
    dataEditor: (props: any) => <OwnershipDataEditor {...props} />,
  },
  {
    alias: CashFlowColumnAlias.ENTITY,
    value: CashFlowColumnValue.ENTITY,
    gridType: 'string',
    dbType: 'string',
    isRequired: true,
    displayUndefined: true,
    valueViewer: (props: any) => <EntityValueViewer {...props} />,
    dataEditor: (props: any) => <EntityDataEditor {...props} />,
  },
  {
    alias: CashFlowColumnAlias.ALL_SECURITY,
    value: CashFlowColumnValue.ALL_SECURITY,
    gridType: 'string',
    dbType: 'string',
    isRequired: false,
    displayUndefined: true,
    valueViewer: (props: any) => <SecurityValueViewer {...props} />,
    dataEditor: (props: any) => <SecurityDataEditor {...props} />,
  },
];
